// src/components/rooms/PropertyRoomsTab.js

import React, { useState, useEffect } from 'react';
import { 
    PlusCircle, Edit, Trash2, LayoutGrid, 
    List as ListIcon, Bed, Layers, AlertTriangle, X, Upload, Info 
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api';
import AddRoomModal from './AddRoomModal';
import BulkUploadModal from './BulkUploadModal';

// --- Import Styles ---
import '../../styles/RoomsScreen.css';

const PropertyRoomsTab = ({ property }) => {
    // --- State ---
    const [rooms, setRooms] = useState([]);
    const [viewMode, setViewMode] = useState('list');
    const [isLoading, setIsLoading] = useState(true);

    // --- Modal State ---
    const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    
    // --- Selected Item State ---
    const [selectedRoom, setSelectedRoom] = useState(null); 
    const [roomToDelete, setRoomToDelete] = useState(null); 

    // --- Dynamic Configuration State ---
    const [roomTypes, setRoomTypes] = useState([]);
    const [floorOptions, setFloorOptions] = useState([]);

    // --- Fetch Data on Mount ---
    useEffect(() => {
        const loadRoomData = async () => {
            setIsLoading(true);
            try {
                const [configData, roomsData] = await Promise.all([
                    api.getRoomConfig(),
                    api.getRooms(property.id)
                ]);

                if (configData) {
                    setRoomTypes(configData.roomTypes || []);
                    setFloorOptions(configData.floorOptions || []);
                }

                if (Array.isArray(roomsData)) {
                    const mapped = roomsData.map(r => ({
                        ...r,
                        number: r.room_number 
                    }));
                    setRooms(mapped);
                }

            } catch (error) {
                console.error("Failed to load room data", error);
                //toast.error("Could not load room settings.");
            } finally {
                setIsLoading(false);
            }
        };

        if (property && property.id) {
            loadRoomData();
        }
    }, [property.id]);

    // --- Actions ---

    const handleOpenAdd = () => {
        setSelectedRoom(null); 
        setIsAddEditModalOpen(true);
    };

    const handleEdit = (room) => {
        setSelectedRoom(room); 
        setIsAddEditModalOpen(true);
    };

    const handleDeleteClick = (room) => {
        setRoomToDelete(room);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setRoomToDelete(null);
    };

    const executeDelete = async () => {
        if (!roomToDelete) return;

        try {
            await api.deleteRoom(roomToDelete.id);
            setRooms(rooms.filter(r => r.id !== roomToDelete.id));
            toast.success('Room deleted successfully');
            closeDeleteModal();
        } catch (error) {
            console.error(error);
            toast.error(error.message || 'Failed to delete room');
        }
    };

    const handleSaveRoom = async (roomData) => {
        try {
            const payload = {
                propertyId: property.id,
                room_number: roomData.number,
                type: roomData.type,
                floor: roomData.floor
            };

            if (roomData.id) {
                // UPDATE
                const updatedRoom = await api.updateRoom(roomData.id, payload);
                setRooms(prevRooms => prevRooms.map(r => 
                    r.id === roomData.id 
                    ? { ...r, ...updatedRoom, number: updatedRoom.room_number } 
                    : r
                ));
                toast.success(`Room ${updatedRoom.room_number} updated!`);
            } else {
                // CREATE
                const savedRoom = await api.createRoom(payload);
                setRooms(prevRooms => [...prevRooms, {
                    ...savedRoom,
                    id: savedRoom.id, 
                    number: savedRoom.room_number,
                    status: 'clean'
                }]);
                toast.success(`Room ${savedRoom.room_number} added!`);
            }
            
            setIsAddEditModalOpen(false); 
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Failed to save room");
        }
    };

    const handleBulkSuccess = (message) => {
        toast.success(message);
        setIsLoading(true);
        api.getRooms(property.id)
            .then(roomsData => {
                const mapped = roomsData.map(r => ({ ...r, number: r.room_number }));
                setRooms(mapped);
            })
            .catch(err => toast.error("Failed to refresh rooms"))
            .finally(() => setIsLoading(false));
    };

    if (isLoading) {
        return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading room data...</div>;
    }

    return (
        <div className="property-group-content1 rooms-tab-container">
            {/* --- Configuration Summary Legend --- */}
            <div className="config-legend">
                {/* Room Types Legend */}
                <div className="legend-column">
                    <h5 className="legend-title">
                        <Bed size={16} /> Available Room Types
                    </h5>
                    <div className="legend-badges">
                        {roomTypes.map(type => (
                            <span key={type} className="legend-badge">
                                {type}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Floor Options Legend */}
                <div className="legend-column">
                    <h5 className="legend-title">
                        <Layers size={16} /> Available Floors
                    </h5>
                    <div className="legend-badges">
                        {floorOptions.map(floor => (
                            <span key={floor} className="legend-badge">
                                {floor}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Info Note */}
                <div className="legend-note">
                    <Info size={14} />
                    <span>Please ensure your bulk upload file uses these exact names for Type and Floor mapping.</span>
                </div>
            </div>

            {/* --- Header & Controls --- */}
            <div className="rooms-controls-header">
                <div className="rooms-header-left">
                    <h3 className="tab-content-title" style={{ margin: 0 }}>Rooms</h3>
                    
                    <div className="view-toggle">
                        <button 
                            onClick={() => setViewMode('grid')}
                            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                            title="Grid View"
                        >
                            <LayoutGrid size={16} />
                        </button>
                        <button 
                            onClick={() => setViewMode('list')}
                            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                            title="List View"
                        >
                            <ListIcon size={16} />
                        </button>
                    </div>
                </div>

                <div className="rooms-header-actions">
                    <button 
                        className="button button-secondary"
                        onClick={() => setIsBulkModalOpen(true)}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        <Upload size={16} />
                        <span>Bulk Create</span>
                    </button>

                    <button 
                        className="action-button-small" 
                        onClick={handleOpenAdd}
                    >
                        <PlusCircle size={16} />
                        <span>Add Room</span>
                    </button>
                </div>
            </div>

            {/* --- Grid View --- */}
            {viewMode === 'grid' && (
                <div className="rooms-grid">
                    {rooms.map(room => (
                        <div key={room.id} className="room-card">
                            <div className="room-card-header">
                                <div className="room-icon-wrapper">
                                    <Bed size={20} />
                                </div>
                                <div className="device-actions">
                                    <button className="icon-button" onClick={() => handleEdit(room)}>
                                        <Edit size={16} />
                                    </button>
                                    <button 
                                        className="icon-button" 
                                        onClick={() => handleDeleteClick(room)} 
                                        style={{ color: '#ef4444' }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                            <h4 className="room-number">{room.number}</h4>
                            <p className="room-type">{room.type}</p>
                            <div className="room-floor-badge">
                                <Layers size={14} />
                                <span>{room.floor}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* --- List View --- */}
            {viewMode === 'list' && (
                <div className="rooms-list-wrapper">
                    <table className="rooms-table">
                        <thead>
                            <tr>
                                <th>Room Number</th>
                                <th>Type</th>
                                <th>Floor</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rooms.map((room) => (
                                <tr key={room.id}>
                                    <td style={{ fontWeight: '500', color: '#0f172a' }}>{room.number}</td>
                                    <td>{room.type}</td>
                                    <td>{room.floor}</td>
                                    <td>
                                        <div className="room-actions-cell">
                                            <button className="icon-button" onClick={() => handleEdit(room)}>
                                                <Edit size={16} />
                                            </button>
                                            <button 
                                                className="icon-button" 
                                                onClick={() => handleDeleteClick(room)} 
                                                style={{ color: '#ef4444' }}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* --- Modals --- */}
            <AddRoomModal 
                isOpen={isAddEditModalOpen} 
                onClose={() => setIsAddEditModalOpen(false)}
                onSave={handleSaveRoom}
                existingRooms={rooms} 
                roomTypes={roomTypes}
                floorOptions={floorOptions}
                roomToEdit={selectedRoom}
            />

            <BulkUploadModal 
                isOpen={isBulkModalOpen}
                onClose={() => setIsBulkModalOpen(false)}
                onSuccess={handleBulkSuccess}
                propertyId={property.id}
            />

            {/* Inline Delete Modal using Class Names */}
            {isDeleteModalOpen && (
                <div className="room-modal-backdrop">
                    <div className="room-modal-content">
                        <div className="room-modal-header">
                            <h3 className="room-modal-title danger">
                                <AlertTriangle size={20} />
                                Confirm Deletion
                            </h3>
                            <button className="room-modal-close" onClick={closeDeleteModal}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="room-modal-body">
                            <p className="delete-warning-text">
                                Are you sure you want to delete <strong>Room {roomToDelete?.number}</strong>? 
                                <br />
                                This action cannot be undone.
                            </p>

                            <div className="room-modal-actions">
                                <button 
                                    type="button" 
                                    className="button button-secondary" 
                                    onClick={closeDeleteModal}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="button" 
                                    style={{ backgroundColor: '#ef4444', color: 'white', border: 'none' }}
                                    onClick={executeDelete}
                                >
                                    Yes, Delete Room
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropertyRoomsTab;